/**
 * MVP module — pathway ranking engine (Phase B).
 * Spec: docs/reference/mvp/IMPLEMENTATION_PLAN.md §1 (contract) + §5 (rules).
 * Pure function: no fetching, no React, no DB. Reference data arrives via
 * `EngineDeps` + the MVP catalog; callers wire MipsDataService.
 *
 * Per-pathway flow:
 *   quality (quality.ts) · cost (cost.ts) · IA (ia.ts / pass-through) · PI
 *   (pass-through, MVP-invariant) → reweight set → weight lookup (weights.ts,
 *   Appendix B T1/T2) → single-category terminal override (score = threshold)
 *   → CPB → final score → dollars (payment-adjustment.ts).
 */
import { computeAdjustmentPct } from '../payment-adjustment';
import { scoreMvpQuality } from './quality';
import { scoreMvpCost } from './cost';
import { scoreMvpIa } from './ia';
import { resolveWeights, type ReweightedCategory } from './weights';
import { parametersForYear, type ParameterSet } from './parameters/py2026';
import type {
  CategoryResult, ConfidenceFlag, EngineDeps, EngineInputs, MvpCatalogYear,
  PathwayId, PathwayResult, RankedReport,
} from './types';
import type { MvpDefinition } from '../mips-data-service';

const ENGINE_RULE_VERSIONS: Record<string, string> = {
  quality: 'mvp-quality-2026.1',
  cost: 'cost-sd-band-py2024 (v1: prior decile points)',
  ia: 'mvp-ia-flat40-2026.1',
  weights: 'trad-appendix-b-2026',
  improvement: 'achievement-pct-x10pct-2026',
};

export function rankPathways(
  inputs: EngineInputs,
  catalog: MvpCatalogYear,
  deps: EngineDeps,
  year: number = inputs.profile.programYear
): RankedReport {
  const params = parametersForYear(year);
  if (!params) return []; // no MVP parameter set for this year — module inert

  const results: PathwayResult[] = [
    scorePathway('traditional', null, inputs, deps, params),
    ...catalog.map(mvp => scorePathway(mvp.mvpId, mvp, inputs, deps, params)),
  ];
  return results.sort((a, b) => b.finalScore - a.finalScore);
}

function applyPopHealthOverride(
  popHealth: EngineInputs['popHealth'],
  override?: { m479?: number | null; m484?: number | null }
): EngineInputs['popHealth'] {
  if (!override) return popHealth;
  const apply = (cur: EngineInputs['popHealth']['m479'], v: number | null | undefined) =>
    v === undefined ? cur : (v === null ? null : { points: v, caseMinMet: true, source: 'assumption' as const });
  return { m479: apply(popHealth.m479, override.m479), m484: apply(popHealth.m484, override.m484) };
}

export function scorePathway(
  pathwayId: PathwayId,
  mvp: MvpDefinition | null,          // null = Traditional
  inputs: EngineInputs,
  deps: EngineDeps,
  params: ParameterSet
): PathwayResult {
  const { profile, entity, assumptions } = inputs;
  const isTraditional = mvp == null;
  const flags = new Set<ConfidenceFlag>();

  // ---- Quality (what-if pop-health overlay applied first) ----
  const popHealth = applyPopHealthOverride(inputs.popHealth, assumptions.popHealthOverride);
  const quality = scoreMvpQuality({
    counts: inputs.qualityCounts,
    qualityPool: isTraditional
      ? [...new Set(inputs.qualityCounts.map(c => c.measureId))] // Traditional: open catalog — whatever has data
      : mvp.qualityMeasureIds,
    popHealth,
    isSmallPractice: profile.isSmallPractice,
    priorYear: inputs.priorYear,
    dedupCollectionTypes: inputs.dedupCollectionTypes,
    params,
    deps,
    requiredCount: inputs.requiredQualityCountOverride
      ?? (isTraditional ? params.traditionalRequiredQualityMeasures : params.mvpRequiredQualityMeasures),
    includePopHealth: !isTraditional, // population health is MVP-only (foundational layer)
    outcomeSlotWaived: inputs.outcomeSlotWaived,
    reservedSlotRule: inputs.reservedSlotRule,
    prOverrides: assumptions.perMeasurePR,
  });
  quality.flags.forEach(f => flags.add(f));

  // ---- Cost ----
  const cost = scoreMvpCost({
    costScores: inputs.costScores,
    costPool: isTraditional ? null : mvp.costMeasureIds,
    priorYear: inputs.priorYear,
    params,
  });
  if (inputs.costYear && inputs.costYear.used < inputs.costYear.performance) flags.add('stale-cost-year');

  // ---- IA ----
  // MVP: binary (1 activity in the pathway inventory = 100%).
  // Traditional: pass-through from the existing IA calculator/modeled value.
  const iaMvp = isTraditional
    ? null
    : scoreMvpIa({
        iaActivityIds: inputs.iaActivityIds,
        iaPool: mvp.iaMeasureIds,
        iaWillAttest: assumptions.iaWillAttest,
        params,
      });
  const iaPct = isTraditional ? inputs.traditionalIaPct : (iaMvp as NonNullable<typeof iaMvp>).pct;

  // ---- PI (MVP-invariant pass-through) ----
  // Small practice auto-reweights UNLESS it submits (qualifying-submission
  // override): explicit willSubmitPI=false forces the reweight even with a score.
  const piSubmits = inputs.piScore != null
    && (!profile.isSmallPractice || assumptions.willSubmitPI !== false);
  const piPct = piSubmits ? inputs.piScore : null;

  // ---- Reweight set + weights ----
  const reweighted = new Set<ReweightedCategory>();
  if (cost.reweighted) reweighted.add('cost');
  if (piPct == null) reweighted.add('pi');
  if (iaPct == null) reweighted.add('ia');
  // Quality is NEVER reweighted under an MVP (MVP guide §6.3 — the one sign flip);
  // v1 doesn't model Traditional quality reweighting either (rare, needs EUC data).

  const { weights, weightSetKey, isSingleCategoryTerminal } = resolveWeights(profile.isSmallPractice, reweighted);

  // ---- CPB (same architecture as Traditional — complexity score from QPP Reports) ----
  const cpb = assumptions.cpbOverride
    ?? (profile.complexityScore != null
      ? Math.min(Math.round(profile.complexityScore * params.cpbMultiplier * 100) / 100, params.cpbCap)
      : 0);
  if (assumptions.cpbOverride == null && profile.complexityScore != null) flags.add('carried-forward-cpb');

  // ---- Final score ----
  let finalScore: number;
  if (isSingleCategoryTerminal) {
    // Trad §6.3: one category left at 100% → final score = performance threshold.
    finalScore = params.performanceThreshold;
    flags.add('terminal-override-75');
  } else {
    finalScore = Math.min(
      params.finalScoreMax,
      (quality.pct * weights.quality
        + (cost.pct ?? 0) * weights.cost
        + (iaPct ?? 0) * weights.ia
        + (piPct ?? 0) * weights.pi) / 100
      + cpb
    );
  }

  if (entity.kind === 'subgroup') flags.add('estimate');

  // ---- Dollars ----
  const basis = profile.allowedCharges;
  let dollars: PathwayResult['dollars'] = { adjustmentPct: null, estImpact: null, basis, caveat: 'Payment-adjustment config not provided' };
  if (inputs.dollarsConfig) {
    const adjustmentPct = computeAdjustmentPct(
      Math.round(finalScore),
      inputs.dollarsConfig.lowScenarioPct,
      inputs.dollarsConfig.scalingFactor
    );
    dollars = {
      adjustmentPct,
      estImpact: basis != null ? adjustmentPct * basis : null,
      basis,
      caveat: adjustmentPct > 0
        ? 'Positive adjustments depend on the CMS budget-neutrality scaling factor, set after the performance year closes'
        : null,
    };
  }

  // ---- Eligibility badges (never hidden rows) ----
  const eligibilityReasons: string[] = [];
  let canReport = true;
  if (!isTraditional && entity.kind === 'group' && profile.multiSpecialty && !profile.isSmallPractice) {
    canReport = false;
    eligibilityReasons.push('Multispecialty group without small-practice status — MVP group reporting unavailable; report as subgroups or individuals');
  }
  if (!isTraditional && mvp.hasCahps) {
    eligibilityReasons.push(`CAHPS measure available — requires separate registration by ${params.registrationWindow.cahpsDeadline}`);
  }

  const mk = (pct: number | null, weight: number, detail: Record<string, unknown>): CategoryResult => ({
    pct,
    weight,
    points: pct != null ? (pct * weight) / 100 : 0,
    detail,
  });

  return {
    pathwayId,
    entity,
    quality: mk(quality.pct, weights.quality, quality.detail as unknown as Record<string, unknown>),
    cost: mk(cost.pct, weights.cost, cost.detail as unknown as Record<string, unknown>),
    ia: mk(iaPct, weights.ia, isTraditional ? { source: 'traditional-ia-passthrough' } : (iaMvp as NonNullable<typeof iaMvp>).detail as unknown as Record<string, unknown>),
    pi: mk(piPct, weights.pi, { source: 'passthrough', mvpInvariant: true, submits: piSubmits }),
    cpb,
    finalScore,
    dollars,
    flags: [...flags],
    eligibility: { canReport, reasons: eligibilityReasons },
    audit: {
      parameterSetYear: params.effectiveFromPY,
      ruleVersions: ENGINE_RULE_VERSIONS,
      weightSetKey,
    },
  };
}
